import { EmeraldError } from 'src/common/api/emerald-error';
import { AbortedError, ProhibitActionError } from 'src/common/api/extended-emerald-error';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import {
  EpsPaymentFailure,
  EpsRefundFailure,
  EpsSignatureCancel,
  EwicHostOffline,
  ProhibitOverTenderRestrictionBehavior,
  SignatureNotVerified,
  TenderAmountExceededEligibilityAmount,
  TenderChangeAvailability,
  TenderExchangeAmountIsOverOrUnderLimitsException,
  TenderExchangeAmountNotAccordingMultiplesException,
  TenderNotAllowedForChange,
  SessionExpired,
} from 'src/common/helpers/error-code';
import { EpsData } from 'src/devices/payment/dtos/eps-data';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { popupAsyncMessage } from '../../messages/business-logic/popup-message';
import { showNotification } from './show-notification';
import { formatInvalidSessionError } from '../../common/helpers/invalid-session-helper';

export const showTenderingError = async (err: EmeraldError, epsData?: EpsData): Promise<void> => {
  if (err instanceof AbortedError) {
    return;
  }

  if (err.code === SessionExpired) {
    return popupAsyncMessage(formatInvalidSessionError(err.message));
  }

  if (err instanceof ProhibitActionError) {
    if (err.code === ProhibitOverTenderRestrictionBehavior) {
      return popupAsyncMessage({ message: err.message, type: EmeraldDialogTypes.Info });
    } else {
      return popupAsyncMessage({ message: err.message, type: EmeraldDialogTypes.Error });
    }
  }

  // These Tender exchange errors need to be handled by caller
  if (
    err.code === TenderExchangeAmountIsOverOrUnderLimitsException ||
    err.code === TenderExchangeAmountNotAccordingMultiplesException
  ) {
    return;
  }

  switch (err.code) {
    case EpsPaymentFailure:
      return showNotification('EPS_Declined_Caption', 'EPS_Declined_Sub_Message');
    case EpsRefundFailure:
      return showNotification(
        'EPS_Refund_Declined_Caption',
        'EPS_Refund_Declined_Sub_Message',
        EmeraldDialogTypes.Error,
      );
    case EpsSignatureCancel:
      return showNotification('EPS_Declined', 'EPS_PaymentCancelledByCustomerMessage');
    case TenderAmountExceededEligibilityAmount:
      return showTenderEligibilityAmount(err, epsData);
    case TenderNotAllowedForChange: {
      const { shouldDisplayTenderNotAllowedForChangeErrorMessage } = getCurrentXLRConfig();
      if (epsData && !shouldDisplayTenderNotAllowedForChangeErrorMessage) {
        return showNotification('CashBackProhibitedTitle', 'CashBackProhibitedMessage');
      } else {
        return showNotification('TenderNotAllowedForChangeHeading', 'TenderNotAllowedForChange');
      }
    }
    case TenderChangeAvailability:
      return showNotification('TenderNotAllowedForChangeHeading', 'TenderNotAllowedForChange');
    case SignatureNotVerified:
      return showNotification('EPS_Declined_Caption', 'SignatureNotVerified');
    case EwicHostOffline:
      return showNotification('UnableToProcessPayment', 'HostOfflineError');
    default: {
      if (epsData) {
        return showNotification('EPS_Payment_Failure', '');
      } else {
        return popupAsyncMessage(err);
      }
    }
  }
};

const showTenderEligibilityAmount = async (err: EmeraldError, epsData?: EpsData): Promise<void> => {
  if (!epsData) {
    return showNotification(
      'Tender_Restriction',
      'Tender_Eligibility_Amount',
      EmeraldDialogTypes.Info,
      undefined,
      { arg: convertCurrency(err.data?.eligibility?.maxEligibleAmount) },
    );
  } else if (err.data?.eligibility?.maxEligibleAmount) {
    await showNotification(
      'Tender_Restriction',
      'TenderAmountExceededEligibilityAmountWithAmountArgs',
      EmeraldDialogTypes.Info,
      undefined,
      {
        arg: epsData?.card.cardName || '',
        arg2: convertCurrency(err.data.eligibility.maxEligibleAmount),
      },
    );
  } else {
    await showNotification(
      'Tender_Restriction',
      'TenderAmountExceededEligibilityAmountWithoutAmountArgs',
      EmeraldDialogTypes.Info,
      undefined,
      {
        arg: epsData?.card.cardName || '',
      },
    );
  }
};
